import type { ColorRef } from './color';

/** A semantic color: one primitive reference per theme. */
export type ThemedColor = { light: ColorRef; dark: ColorRef };

const t = (light: ColorRef, dark: ColorRef): ThemedColor => ({ light, dark });

/**
 * Semantic colors (docs/06-design-system.md › Semantic colors). Components use ONLY these,
 * as CSS variables: `color.bg.surfaceRaised` → `--gg-color-bg-surface-raised`.
 * If a contrast pair fails, fix the value HERE, never in a component.
 */
export const semantic = {
  bg: {
    canvas: t('neutral.25', 'neutral.950'),
    surface: t('neutral.0', 'neutral.900'),
    surfaceRaised: t('neutral.0', 'neutral.800'),
    sunken: t('neutral.50', '#0E0B08'),
    muted: t('neutral.100', 'neutral.800'),
    inverse: t('neutral.900', 'neutral.50'),
  },
  fg: {
    default: t('neutral.900', 'neutral.50'),
    muted: t('neutral.600', 'neutral.300'),
    subtle: t('neutral.500', 'neutral.400'),
    inverse: t('neutral.0', 'neutral.900'),
    link: t('blue.700', 'blue.300'),
  },
  border: {
    default: t('neutral.200', 'neutral.700'),
    strong: t('neutral.300', 'neutral.600'),
    focus: t('blue.600', 'blue.300'),
  },
  action: {
    primary: {
      bg: t('berry.500', 'berry.400'),
      bgHover: t('berry.600', 'berry.300'),
      bgActive: t('berry.700', 'berry.200'),
      fg: t('neutral.0', 'neutral.950'),
      pop: t('berry.700', 'berry.600'),
    },
    secondary: {
      bg: t('neutral.0', 'neutral.800'),
      bgHover: t('neutral.50', 'neutral.700'),
      fg: t('neutral.900', 'neutral.50'),
      border: t('neutral.300', 'neutral.600'),
      pop: t('neutral.300', 'neutral.950'),
    },
    fun: {
      bg: t('blue.500', 'blue.400'),
      bgHover: t('blue.400', 'blue.300'),
      fg: t('neutral.950', 'neutral.950'),
      pop: t('blue.700', 'blue.700'),
    },
    ghost: {
      bgHover: t('neutral.100', 'neutral.800'),
      fg: t('neutral.800', 'neutral.100'),
    },
    danger: {
      fg: t('crimson.500', 'crimson.300'),
      border: t('crimson.300', 'crimson.700'),
      bgHover: t('crimson.50', 'crimson.900'),
    },
  },
  interactive: {
    selectedBg: t('blue.50', 'blue.900'),
    selectedFg: t('blue.800', 'blue.100'),
    indicator: t('blue.600', 'blue.400'),
  },
  accent: {
    bg: t('berry.500', 'berry.400'),
    bgSubtle: t('berry.50', 'berry.900'),
    fg: t('berry.700', 'berry.100'),
    border: t('berry.200', 'berry.700'),
  },
  highlight: {
    bg: t('gold.50', 'gold.900'),
    bgStrong: t('gold.400', 'gold.400'),
    fg: t('gold.800', 'gold.100'),
    icon: t('gold.500', 'gold.300'),
  },
  status: {
    success: {
      bg: t('mint.50', 'mint.900'),
      fg: t('mint.900', 'mint.100'),
      border: t('mint.100', 'mint.700'),
      icon: t('mint.500', 'mint.300'),
    },
    warning: {
      bg: t('orange.50', 'orange.900'),
      fg: t('orange.900', 'orange.100'),
      border: t('orange.100', 'orange.700'),
      icon: t('orange.500', 'orange.300'),
    },
    danger: {
      bg: t('crimson.50', 'crimson.900'),
      fg: t('crimson.900', 'crimson.100'),
      border: t('crimson.100', 'crimson.700'),
      icon: t('crimson.500', 'crimson.300'),
    },
    info: {
      bg: t('blue.50', 'blue.900'),
      fg: t('blue.900', 'blue.100'),
      border: t('blue.100', 'blue.700'),
      icon: t('blue.600', 'blue.300'),
    },
  },
  claim: {
    mine: {
      bg: t('blue.50', 'blue.900'),
      fg: t('blue.800', 'blue.100'),
      icon: t('blue.500', 'blue.300'),
    },
    others: {
      bg: t('neutral.100', 'neutral.800'),
      fg: t('neutral.600', 'neutral.300'),
    },
  },
} as const;

export type SemanticColors = typeof semantic;

/** Usage notes shown on /styles. Keyed by dotted path under `semantic`. */
export const semanticUsage: Record<string, string> = {
  'bg.canvas': 'App background (cream)',
  'bg.surface': 'Cards, sheets',
  'bg.surfaceRaised': 'Dialogs, menus, bottom sheets',
  'bg.sunken': 'Inputs, wells, calendar cells',
  'bg.muted': 'Pressed rows, skeleton base',
  'bg.inverse': 'Tooltips, snackbar toasts',
  'fg.default': 'Body text',
  'fg.muted': 'Secondary text',
  'fg.subtle': 'Icons, chevrons, decoration. Not for text (below 4.5:1).',
  'fg.inverse': 'Text on inverse backgrounds',
  'fg.link': 'Links',
  'border.default': 'Dividers, card borders',
  'border.strong': 'Input borders',
  'border.focus': 'Focus ring',
  'action.primary.bg': 'Primary button (berry, white text)',
  'action.fun.bg': 'The blue "fun" button (dark text): Claim it, Paste a list',
  'action.secondary.bg': 'Secondary button',
  'action.danger.fg': 'Outlined destructive button',
  'interactive.selectedBg': 'Selected chips, active nav item',
  'interactive.indicator': 'Active tab underline',
  'accent.bgSubtle': 'Celebration surfaces, recipient task card',
  'highlight.bgStrong': 'Most-wanted star fill (with dark text)',
  'claim.mine.bg': '"You\'re getting this 🎁"',
  'claim.others.bg': '"Claimed by Sam" (muted row)',
};

/** Decorative confetti colors (TS only; not emitted as CSS vars). */
export const confettiColors: readonly ColorRef[] = [
  'berry.400',
  'blue.500',
  'gold.400',
  'mint.300',
  'plum.300',
];

/**
 * Pairs checked for WCAG AA in BOTH themes by tokens.test.ts and shown on /styles.
 * `fg` and `bg` are dotted semantic paths, or a primitive ref prefixed with `=`.
 * kind 'text' → 4.5:1, 'ui' → 3:1 (large text, icons, borders, focus rings).
 */
export type ContrastPair = { fg: string; bg: string; kind: 'text' | 'ui'; label: string };

export const contrastPairs: readonly ContrastPair[] = [
  { fg: 'fg.default', bg: 'bg.canvas', kind: 'text', label: 'Body text on canvas' },
  { fg: 'fg.default', bg: 'bg.surface', kind: 'text', label: 'Body text on cards' },
  { fg: 'fg.muted', bg: 'bg.surface', kind: 'text', label: 'Secondary text on cards' },
  { fg: 'fg.muted', bg: 'bg.canvas', kind: 'text', label: 'Secondary text on canvas' },
  { fg: 'fg.link', bg: 'bg.surface', kind: 'text', label: 'Links on cards' },
  { fg: 'fg.subtle', bg: 'bg.surface', kind: 'ui', label: 'Placeholder / meta' },
  { fg: 'fg.inverse', bg: 'bg.inverse', kind: 'text', label: 'Tooltip text' },
  { fg: 'action.primary.fg', bg: 'action.primary.bg', kind: 'text', label: 'Primary button' },
  { fg: 'action.fun.fg', bg: 'action.fun.bg', kind: 'text', label: 'Fun (blue) button' },
  { fg: 'action.secondary.fg', bg: 'action.secondary.bg', kind: 'text', label: 'Secondary button' },
  { fg: 'action.danger.fg', bg: 'bg.surface', kind: 'text', label: 'Danger button text' },
  { fg: 'accent.fg', bg: 'accent.bgSubtle', kind: 'text', label: 'Accent text on subtle accent' },
  {
    fg: 'interactive.selectedFg',
    bg: 'interactive.selectedBg',
    kind: 'text',
    label: 'Selected chip',
  },
  { fg: 'claim.mine.fg', bg: 'claim.mine.bg', kind: 'text', label: '"You\'re getting this"' },
  { fg: 'claim.others.fg', bg: 'claim.others.bg', kind: 'text', label: '"Claimed by Sam"' },
  { fg: 'highlight.fg', bg: 'highlight.bg', kind: 'text', label: 'Highlight text' },
  { fg: '=neutral.950', bg: 'highlight.bgStrong', kind: 'text', label: 'Dark text on gold' },
  { fg: 'status.success.fg', bg: 'status.success.bg', kind: 'text', label: 'Success banner' },
  { fg: 'status.warning.fg', bg: 'status.warning.bg', kind: 'text', label: 'Warning banner' },
  { fg: 'status.danger.fg', bg: 'status.danger.bg', kind: 'text', label: 'Danger banner' },
  { fg: 'status.info.fg', bg: 'status.info.bg', kind: 'text', label: 'Info banner' },
  { fg: 'border.focus', bg: 'bg.canvas', kind: 'ui', label: 'Focus ring on canvas' },
  { fg: 'border.focus', bg: 'bg.surface', kind: 'ui', label: 'Focus ring on cards' },
  { fg: 'interactive.indicator', bg: 'bg.surface', kind: 'ui', label: 'Active tab indicator' },
];
